/**
 * Asserts that the given argument is of a specific type or else return `null`.
 *
 * @template T - The type to check against.
 * @param arg - The argument to check.
 * @param check - A function that performs the type check.
 * @returns The argument if it passes the type check, otherwise `null`.
 */
export function assertIsTypeOrElseNull<T>(arg: unknown, check: (val: unknown) => val is T): T | null {
  try {
    assertIsType(arg, check);
    return arg;
  } catch {
    return null;
  }
}

/**
 * Asserts that the provided value passes a specified predicate check
 * @param arg A value you want to assert is a specified type
 * @param check A predicate function
 */
export function assertIsType<T>(arg: unknown, check: (val: unknown) => val is T): asserts arg is T {
  if (!check(arg)) {
    throw new Error(`Violators found: ${JSON.stringify(arg)}`);
  }
}

/**
 * Asserts that every value in an array passes a specified predicate check
 * @param arg A value you want to assert is an array where every value is a specified type
 * @param check A predicate function
 */
export function assertIsTypedArray<T>(arg: unknown, check: (val: unknown) => val is T): asserts arg is T[] {
  if (!Array.isArray(arg)) {
    throw new Error(`Not an array: ${JSON.stringify(arg)}`);
  }
  if (arg.some((item) => !check(item))) {
    const failingArgs = arg.filter((item) => !check(item));
    throw new Error(`Violators found on items: ${JSON.stringify(failingArgs)}`);
  }
}

/**
 * A predicate function that lets us narrow the type of the candidate paramater to an object. This is
 * really useful for API boundaries that return JSON objects that we want to type check. If an object
 * passes this check then you can then check that arbitrary paramaters exist on that object without having
 * to declare it as an any type.
 * @param candidate Value whose type we are checking
 */
export function isObjectLike(candidate: unknown): candidate is Record<string, unknown> {
  return typeof candidate === 'object' && candidate !== null && !Array.isArray(candidate);
}

export function isNullOrUndefined(candidate: unknown): boolean {
  return candidate === undefined || candidate === null;
}

export function has<P extends PropertyKey>(target: unknown, property: P): target is { [K in P]: unknown } {
  // The `in` operator throws a `TypeError` for non-object values.
  return isObjectLike(target) && property in target;
}

export function isNonEmptyString(candidate: unknown): candidate is string {
  return typeof candidate === 'string' && !!candidate.length;
}

export function isEmptyString(candidate: unknown): candidate is string {
  return typeof candidate === 'string' && !candidate.length;
}

export function isArrayOfStrings(arg: unknown): boolean {
  return Array.isArray(arg) && !arg.some((item) => typeof item !== 'string');
}

/**
 * Gets the values of an object and turns them into a type.
 * Thanks to Matt Pocock: https://www.youtube.com/watch?v=jjMbPt_H3RQ
 * Use like this...
 * ```
 * const LOG_LEVEL = {
 *  DEBUG: "DEBUG",
 *  WARNING: "WARNING",
 *  ERROR: "ERROR",
 * } as const;
 *
 * type LogLevel = ObjectValues<typeof LOG_LEVEL>;
 * ```
 */
export type ObjectValues<T> = T[keyof T];

/**
 * ```
 * type Foo = { bar: { baz: string } };
 * type Optional = RecursiveOptional<Foo, 'baz'>;
 * const example: Optional = { bar: { baz: 'hello' } }; // Valid.
 * ```
 */
export type RecursiveOptional<T, K extends PropertyKey> = T extends object
  ? {
      [P in keyof T as P extends K ? never : P]: RecursiveOptional<T[P], K>;
    } & {
      [P in keyof T as P extends K ? P : never]?: RecursiveOptional<T[P], K>;
    }
  : T;
